import { useEffect, useState } from "react";
import axios from "axios";
import { ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Lightweight thumbs-up / thumbs-down panel under each public story. The
 *  backend rate-limits per IP+slug+direction every 24 h; we ALSO track the
 *  user's choice in localStorage so the UI feels immediate (selected state
 *  persists across page reloads on the same browser).
 *
 *  Renders nothing until counts arrive — keeps the page from flickering. */
export default function StoryReactions({ slug, initialHelpful = 0, initialNotHelpful = 0 }) {
  const [helpful, setHelpful] = useState(initialHelpful);
  const [notHelpful, setNotHelpful] = useState(initialNotHelpful);
  const [submitting, setSubmitting] = useState(null); // "up" | "down" | null
  const [chosen, setChosen] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(`reacted:${slug}`); // "up" | "down" | null
  });

  // Sync if the parent loads with newer counts (e.g. story refresh).
  useEffect(() => {
    setHelpful(initialHelpful);
    setNotHelpful(initialNotHelpful);
  }, [initialHelpful, initialNotHelpful]);

  const react = async (direction) => {
    if (submitting || chosen === direction) return;
    setSubmitting(direction);
    try {
      const r = await axios.post(`${API}/stories/${slug}/react`, { reaction: direction });
      setHelpful(r.data.helpful_count);
      setNotHelpful(r.data.not_helpful_count);
      if (r.data.counted) {
        setChosen(direction);
        try { localStorage.setItem(`reacted:${slug}`, direction); } catch (_e) { /* ignored */ }
      }
    } catch (_e) { /* tolerated */ }
    finally { setSubmitting(null); }
  };

  const total = helpful + notHelpful;

  return (
    <div className="mt-12 pt-8 border-t border-white/10" data-testid="story-reactions">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mb-3 text-center">
        Was this story meaningful to you?
      </div>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => react("up")}
          disabled={submitting !== null}
          aria-pressed={chosen === "up"}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full border transition-colors ${chosen === "up" ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)]" : "border-white/10 hover:border-white/30"}`}
          data-testid="story-reaction-up"
        >
          {submitting === "up" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
          <span className="text-sm">Yes</span>
          {helpful > 0 && <span className="text-xs text-[color:var(--muted)]">· {helpful}</span>}
        </button>
        <button
          onClick={() => react("down")}
          disabled={submitting !== null}
          aria-pressed={chosen === "down"}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-full border transition-colors ${chosen === "down" ? "border-red-400 bg-red-500/10 text-red-300" : "border-white/10 hover:border-white/30"}`}
          data-testid="story-reaction-down"
        >
          {submitting === "down" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
          <span className="text-sm">Not for me</span>
          {notHelpful > 0 && <span className="text-xs text-[color:var(--muted)]">· {notHelpful}</span>}
        </button>
      </div>
      {chosen && (
        <div className="text-center text-[11px] text-[color:var(--muted)] mt-3">
          {chosen === "up"
            ? "Thank you for the encouragement."
            : "Thank you — your feedback helps shape future stories."}
        </div>
      )}
      {total >= 5 && (
        <div className="text-center text-[10px] uppercase tracking-[0.2em] text-[color:var(--muted)] mt-4">
          {total.toLocaleString()} {total === 1 ? "reader has" : "readers have"} responded
        </div>
      )}
    </div>
  );
}
